import React, {useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import './ActiveProjectUtilizationWidget.scss';
import ArrWidgetWrapper from '../../../common/arr-widget-wrapper/ArrWidgetWrapper';
import ArrBarStripe from '../../../common/arr-bar-stripe/ArrBarStripe';
import {t} from 'i18next';
import {
  useActiveProjectUtilization,
  useChangeRoleToRMU,
} from '../../../../hooks/dashboard/useDashboard';
import {RootState} from '../../../../redux/store';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {setAuthenticatedUser} from '../../../../redux/slice/authenticatedUserSlice';
const mappingColor: Record<string, string> = {
  Database: '#00D0DB',
  Index: '#C4AAEB',
  Workspace: '#89A6FF',
};

const ActiveProjectUtilizationWidget: React.FC<{
  entityId?: number;
  updatedAt?: number;
}> = ({entityId, updatedAt = 0}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const {data, isLoading, isError, refetch} = useActiveProjectUtilization(
    entityId ?? loginUserData?.entityId ?? 0
  );

  const {mutate: changeRoleToRMU} = useChangeRoleToRMU();

  // Refetch data on updatedAt changes
  useEffect(() => {
    refetch();
  }, [updatedAt]);

  const handleProjectClick = (projectId: number) => {
    changeRoleToRMU(
      {projectId: projectId},
      {
        onSuccess: (result) => {
          const mergedUser = {
            ...loginUserData,
            projectId: result?.projectId,
            securityGroupId: result?.securityGroupId,
            baseRoleId: result?.baseRoleId,
            roleId: result?.roleId,
            roleName: result?.roleName,
            landingPage: result?.landingPage,
          };
          dispatch(setAuthenticatedUser(mergedUser));
          navigate(result?.landingPage);
        },
      }
    );
  };
  return (
    <ArrWidgetWrapper
      title={t('dashboard.activeProjectUtilizationWidget.title')}
      showInfo
      loading={isLoading}
      error={isError}
      tooltipContent={t('dashboard.activeProjectUtilizationWidget.tooltip')}
    >
      <Box className="active-project-utilization-widget">
        {/* Top Summary */}
        <Box className="summary-data">
          <Box className="total-gb-box">
            <Typography
              className="typo-text label2_size heading-text"
              data-testid={`active-project-total-utilization-label`}
            >
              {t('dashboard.activeProjectUtilizationWidget.totalGB')}
            </Typography>
            <Typography
              className="typo-text display2_size"
              data-testid={`active-project-util-total`}
            >
              {data?.total}
            </Typography>
          </Box>
          <Box className="summary-detail-box">
            {data?.lstdomainDashboardUtilizationChart.map((detail) =>
              detail.color ? (
                <Box
                  key={detail.label}
                  className="details"
                  sx={{color: mappingColor[detail.label]}}
                >
                  <Typography
                    className="typo-text label2_size"
                    data-testid={`${detail.label}-label`}
                  >
                    {`${detail.label}:`}
                  </Typography>
                  <Typography
                    className="detail-data-text typo-text body3_size"
                    data-testid={`${detail.label}-data`}
                  >
                    {detail.data}{' '}
                    {t('dashboard.activeProjectUtilizationWidget.gb')}
                  </Typography>
                </Box>
              ) : null
            )}
          </Box>
        </Box>
        {data?.lstdomainDashboardUtilizationChart && (
          <ArrBarStripe
            graphData={data.lstdomainDashboardUtilizationChart.filter(
              (data) => data.color !== null
            )}
            total={data?.total}
            stripeWidth={4.2}
            dataTestId="active-project-utilization-bar-stripe"
          />
        )}

        {/* Table Header */}
        <Box className="table-header">
          <Typography
            className="typo-text label2_size table-header-data active-project-header"
            data-testid={`active-project-utilization-widget-top5active-projects`}>
            {t('dashboard.activeProjectUtilizationWidget.top5ActiveProjects')}
          </Typography>
          <Typography
            className="typo-text label2_size table-header-data"
            data-testid={`active-project-utilization-widget-total-size`}>
            {t('dashboard.activeProjectUtilizationWidget.totalSize')}
          </Typography>
        </Box>

        {/* Table Rows */}
        <Box>
          {data?.lstprojectSizeDetails.map((proj, idx) => (
            <Box
              key={proj.projectID}
              className={`table-row ${idx % 2 === 0 ? 'row-alt' : ''}`}
            >
              <Typography
                className="project-name typo-text subhead5_size"
                data-testid={`${proj.projectID}-name`}
                onClick={() => handleProjectClick(proj.projectID)}
              >
                {proj.projectName}
              </Typography>
              <Box className="activity-col">
                <Typography
                  className="typo-text body3_size"
                  data-testid={`${proj.projectID}-size`}
                >
                  {proj.size} {t('dashboard.activeProjectUtilizationWidget.gb')}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </ArrWidgetWrapper>
  );
};

export default ActiveProjectUtilizationWidget;
